import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Home, Wallet, RefreshCw, Store, Truck, Trash2, RotateCw, Plus, CalendarClock, Ban, Undo2, ScanLine, AlertTriangle, X, ClipboardList, Printer, PackagePlus, CheckCircle2, Layers, Settings, Download, Users } from "lucide-react";
import RequireAuth from "../components/RequireAuth";
import { authApi, logout } from "../lib/auth";
import { LOGO_URL } from "../lib/site";

const SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
const RATE_KEYS = ["S", "M", "L", "XL", "2XL", "3XL", "default"];
const COURIERS = ["Blue Dart", "Delhivery", "DTDC", "Ekart", "Shadowfax", "Xpressbees", "Valmo"];
// Distinctive tone per courier so the packing-desk sees which shelf immediately.
const COURIER_TONE = {
  "Blue Dart":  "bg-blue-600 text-white",
  "Delhivery":  "bg-red-600 text-white",
  "DTDC":       "bg-orange-500 text-white",
  "Ekart":      "bg-teal-600 text-white",
  "Shadowfax":  "bg-fuchsia-600 text-white",
  "Xpressbees": "bg-yellow-400 text-slate-950",
  "Valmo":      "bg-emerald-600 text-white",
};
const normalizeCourier = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("blue"))       return "Blue Dart";
  if (s.includes("delhivery"))  return "Delhivery";
  if (s.includes("dtdc"))       return "DTDC";
  if (s.includes("ekart"))      return "Ekart";
  if (s.includes("shadow"))     return "Shadowfax";
  if (s.includes("xpress") || s.includes("express"))    return "Xpressbees";
  if (s.includes("valmo"))      return "Valmo";
  return raw; // keep unknown values as-is so admin can see them and correct
};

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  shipped: "bg-sky-100 text-sky-700",
  billed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

function ShipmentsContent() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [shipments, setShipments] = useState([]);
  const [eod, setEod] = useState([]);

  const [domain, setDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [rates, setRates] = useState({});
  const [orderName, setOrderName] = useState("");
  const [qty, setQty] = useState({});
  const [syncFrom, setSyncFrom] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [scanResult, setScanResult] = useState(null);

  const updateCourier = async (shipmentId, courier) => {
    try {
      await authApi.post(`/admin/shipments/${shipmentId}/courier`, { courier });
      setScanResult((r) => (r && r.shipment_id === shipmentId ? { ...r, courier } : r));
      toast.success(courier ? `Courier set to ${courier}` : "Courier cleared");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save courier");
    }
  };
  const [manualBill, setManualBill] = useState(null);
  // shape: { order_name: string, sizes: {S:0,M:0,...}, submitting: bool }
  const openManualBill = (code) => {
    const empty = {};
    RATE_KEYS.forEach((k) => { empty[k] = 0; });
    setManualBill({ order_name: (code || "").replace(/^#/, ""), sizes: empty, submitting: false });
  };
  const closeManualBill = () => setManualBill(null);
  const submitManualBill = async () => {
    if (!manualBill || !brandId) return;
    const cleanSizes = Object.fromEntries(Object.entries(manualBill.sizes).map(([k, v]) => [k, Number(v) || 0]).filter(([, v]) => v > 0));
    if (!manualBill.order_name.trim()) return toast.error("Order id required");
    if (Object.keys(cleanSizes).length === 0) return toast.error("Enter at least one size quantity");
    setManualBill((m) => ({ ...m, submitting: true }));
    try {
      const r = await authApi.post("/admin/shipments/manual-bill", {
        user_id: brandId,
        order_name: manualBill.order_name.trim(),
        sizes: cleanSizes,
      });
      toast.success(`✓ ${r.data.order_name} billed manually · ₹${r.data.charge}`);
      setScanResult(null);
      closeManualBill();
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Manual bill failed");
      setManualBill((m) => m ? { ...m, submitting: false } : m);
    }
  };
  const [activeTab, setActiveTab] = useState("pending");
  const [repStart, setRepStart] = useState("");
  const [repEnd, setRepEnd] = useState("");
  const [preview, setPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [packingList, setPackingList] = useState(null);
  const [srUnassigned, setSrUnassigned] = useState([]);
  const [srSyncing, setSrSyncing] = useState(false);
  const [srFilterMode, setSrFilterMode] = useState("contains");
  const [srFilterText, setSrFilterText] = useState("");
  const [srSelected, setSrSelected] = useState([]);
  const [srBulkBrand, setSrBulkBrand] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [srStatus, setSrStatus] = useState(null);

  const brand = brands.find((b) => b.user_id === brandId);

  const loadBrands = useCallback(() => {
    authApi.get("/admin/brands").then((r) => setBrands(r.data.filter((b) => b.role !== "admin"))).catch(() => {});
    authApi.get("/admin/eod-summary").then((r) => setEod(r.data)).catch(() => {});
    authApi.get("/admin/shiprocket/sync-status").then((r) => setSrStatus(r.data || null)).catch(() => {});
  }, []);

  const loadShipments = useCallback((uid) => {
    if (!uid) return setShipments([]);
    const url = uid === "__all__" ? `/admin/shipments?status_filter=pending` : `/admin/shipments?user_id=${uid}`;
    authApi.get(url).then((r) => setShipments(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);
  useEffect(() => {
    loadShipments(brandId);
    if (brand) {
      setDomain(brand.shopify_domain || "");
      setClientId("");
      setClientSecret("");
      setRates(brand.size_rates || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const refreshAll = () => { loadBrands(); loadShipments(brandId); };
  const handleLogout = async () => { await logout(); navigate("/"); };

  const connectShopify = async () => {
    if (!brandId) return toast.error("Select a brand");
    if (!domain || !clientId || !clientSecret) return toast.error("Enter store domain, Client ID and Client Secret");
    try {
      const r = await authApi.post("/admin/brands/shopify", { user_id: brandId, domain, client_id: clientId, client_secret: clientSecret });
      toast.success(`Connected to ${r.data.shop}`);
      setClientId("");
      setClientSecret("");
      loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
  };

  const saveRates = async () => {
    if (!brandId) return toast.error("Select a brand");
    try {
      await authApi.post("/admin/brands/size-rates", { user_id: brandId, rates });
      toast.success("Size rates saved");
      loadBrands();
    } catch (e) { toast.error("Failed to save rates"); }
  };

  const syncShopify = async (fromOverride) => {
    const from = fromOverride !== undefined ? fromOverride : syncFrom;
    try {
      const r = await authApi.post("/admin/shipments/sync", { user_id: brandId, sync_from: from || undefined });
      toast.success(`Fetched ${r.data.fetched}, ${r.data.new} new shipment(s) from ${r.data.stores} store(s)`);
      (r.data.errors || []).forEach((er) => toast.error(`${er.domain}: ${er.error}`, { duration: 8000 }));
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };

  const syncWindow = (days) => {
    const from = days == null ? "" : new Date(Date.now() - days * 864e5).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    setSyncFrom(from);
    syncShopify(from);
  };

  const addManual = async () => {
    if (!brandId) return toast.error("Select a brand");
    if (!orderName) return toast.error("Enter an order name / ID");
    const sizes = {};
    SIZES.forEach((s) => { if (Number(qty[s]) > 0) sizes[s] = Number(qty[s]); });
    if (Object.keys(sizes).length === 0) return toast.error("Enter at least one size quantity");
    try {
      await authApi.post("/admin/shipments", { user_id: brandId, order_name: orderName, sizes });
      toast.success("Shipment added");
      setOrderName(""); setQty({});
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const shipOne = async (id) => {
    const tracking = window.prompt("Tracking number (optional):", "") || "";
    try {
      const r = await authApi.post(`/admin/shipments/${id}/ship`, { tracking_number: tracking });
      toast.success(`Shipped · ${r.data.same_day ? "Same-day" : "Next-day"} (${r.data.expected_ship_date}) · Shopify: ${r.data.shopify}`);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to ship"); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    try { await authApi.delete(`/admin/shipments/${id}`); toast.success("Deleted"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelOne = async (id) => {
    if (!window.confirm("Cancel this order? It won't be shipped or billed. You can revert it later.")) return;
    try { await authApi.post(`/admin/shipments/${id}/cancel`, {}); toast.success("Order cancelled"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const revertOne = async (id) => {
    try { await authApi.post(`/admin/shipments/${id}/revert`, {}); toast.success("Order restored to pending"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to revert"); }
  };

  const scanOrder = async () => {
    const code = scanCode.trim();
    setScanCode("");
    if (!code) return;
    if (!brandId) return toast.error("Select a brand first");
    try {
      const { data: d } = await authApi.post("/admin/shipments/scan", { user_id: brandId, code });
      if (d.result === "shipped") {
        toast.success(`✓ ${d.order_name} → shipped & unbilled (₹${d.charge})`);
        setScanResult({ type: "shipped", ...d });
      } else if (d.result === "duplicate") {
        toast.error(`⚠ Duplicate: ${d.order_name} already ${d.status}`);
        setScanResult({ type: "duplicate", ...d });
      } else if (d.result === "cancelled") {
        toast.error(`${d.order_name} is cancelled — revert it first`);
        setScanResult({ type: "cancelled", ...d });
      } else {
        toast.error(`No matching order for "${code}"`);
        setScanResult({ type: "not_found", code });
        if (mode !== "self") openManualBill(code);
      }
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
  };

  const chargeDay = async () => {
    if (!brandId) return toast.error("Select a brand");
    if (!window.confirm("Charge this brand's wallet for all shipped-and-unbilled shipments?")) return;
    try {
      const r = await authApi.post("/admin/shipments/charge", { user_id: brandId });
      toast.success(`Charged ₹${r.data.charged} for ${r.data.count} shipment(s). Balance ₹${r.data.balance}`);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to charge"); }
  };

  const brandEod = eod.find((e) => e.user_id === brandId);
  const sizesSummary = (s) => Object.entries(s).map(([k, v]) => `${v}×${k}`).join(", ");
  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
  };
  const TABS = ["pending", "shipped", "billed", "cancelled"];
  const tabCount = (t) => shipments.filter((s) => s.status === t).length;
  const dayKey = (d) => { try { return new Date(d).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); } catch { return ""; } };
  const tabShipments = shipments
    .filter((s) => s.status === activeTab)
    .filter((s) => !dateFilter || dayKey(s.order_date) === dateFilter);
  const mode = brand?.shipping_mode || "brand";
  const stores = brand?.stores || [];
  const isAll = brandId === "__all__";
  const brandNameOf = (uid) => {
    const b = brands.find((x) => x.user_id === uid);
    return b ? (b.brand_name || b.email) : uid;
  };
  const fmtSyncAt = (d) => {
    if (!d) return "never synced";
    try { return new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
  };
  const freshnessTone = (d) => {
    if (!d) return "bg-slate-100 text-slate-500 border-slate-300";
    const mins = (Date.now() - new Date(d).getTime()) / 60000;
    if (mins < 30) return "bg-emerald-100 text-emerald-700 border-emerald-300";
    if (mins < 180) return "bg-amber-100 text-amber-700 border-amber-300";
    return "bg-rose-100 text-rose-700 border-rose-300";
  };
  const lastShopifySync = (() => {
    const ts = stores.map((s) => s.last_sync_at).filter(Boolean).map((d) => new Date(d).getTime());
    return ts.length ? new Date(Math.max(...ts)).toISOString() : null;
  })();
  const lastShiprocketSync = srStatus?.finished_at || null;
  const ReadyBadge = ({ s }) =>
    s.tracking_number
      ? <span data-testid={`ready-badge-${s.shipment_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Ready to pack</span>
      : <span data-testid={`new-badge-${s.shipment_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2 py-0.5">New</span>;
  const allPending = shipments.filter((s) => s.status === "pending")
    .filter((s) => !dateFilter || dayKey(s.order_date) === dateFilter);

  const previewStyle = (p) => {
    const size = 256, pad = 18;
    let left = p.x + pad, top = p.y + pad;
    if (left + size > window.innerWidth) left = p.x - size - pad;
    if (top + size > window.innerHeight) top = p.y - size - pad;
    return { position: "fixed", left: Math.max(8, left), top: Math.max(8, top), zIndex: 1000, pointerEvents: "none" };
  };

  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = tabShipments.length > 0 && tabShipments.every((s) => selectedIds.includes(s.shipment_id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : tabShipments.map((s) => s.shipment_id));

  const bulkAction = async (action) => {
    const ids = selectedIds.filter((id) => tabShipments.some((s) => s.shipment_id === id));
    if (ids.length === 0) return toast.error("No orders selected");
    const verb = { ship: "ship", cancel: "cancel", delete: "delete" }[action];
    if (!window.confirm(`${verb.toUpperCase()} ${ids.length} selected order(s)?`)) return;
    try {
      const r = await authApi.post("/admin/shipments/bulk", { action, ids });
      toast.success(`${r.data.affected} order(s) ${verb === "delete" ? "deleted" : verb + "ped"}`);
      setSelectedIds([]);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };

  const setMode = async (newMode) => {
    if (!brandId) return;
    try {
      await authApi.post("/admin/brands/mode", { user_id: brandId, mode: newMode });
      toast.success(newMode === "self" ? "Set to Self-shipped (COD, no wallet)" : "Set to Brand-shipped (wallet)");
      loadBrands();
    } catch (e) { toast.error("Failed to change mode"); }
  };

  const removeStore = async (storeId) => {
    if (!window.confirm("Remove this store connection?")) return;
    try {
      await authApi.delete(`/admin/brands/${brandId}/shopify/${storeId}`);
      toast.success("Store removed");
      loadBrands();
    } catch (e) { toast.error("Failed to remove store"); }
  };

  const settleWeek = async () => {
    if (!window.confirm("Mark all processed (shipped & unsettled) orders as settled for this brand?")) return;
    try {
      const r = await authApi.post("/admin/shipments/settle", { user_id: brandId });
      toast.success(`Settled ₹${r.data.settled} for ${r.data.count} order(s)`);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to settle"); }
  };

  const [packingBusy, setPackingBusy] = useState(false);
  const [walletHistory, setWalletHistory] = useState(null); // { data, filter, loading }

  const openWalletHistory = async () => {
    if (!brandId || isAll) return toast.error("Pick a specific brand first");
    setWalletHistory({ data: null, filter: "all", loading: true });
    try {
      const r = await authApi.get(`/admin/brands/${brandId}/wallet-txns`);
      setWalletHistory({ data: r.data, filter: "all", loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load wallet history");
      setWalletHistory(null);
    }
  };
  const reloadWalletHistory = async (filter) => {
    if (!walletHistory) return;
    setWalletHistory((w) => ({ ...w, loading: true, filter }));
    try {
      const q = filter === "all" ? "" : `?type=${filter}`;
      const r = await authApi.get(`/admin/brands/${brandId}/wallet-txns${q}`);
      setWalletHistory({ data: r.data, filter, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
      setWalletHistory((w) => ({ ...w, loading: false }));
    }
  };
  const closeWalletHistory = () => setWalletHistory(null);

  const [team, setTeam] = useState(null); // { list, newEmail }
  const openTeamModal = async () => {
    setTeam({ list: [], newEmail: "" });
    try {
      const r = await authApi.get("/admin/label-scanners");
      // Preserve any email the admin may have typed while the list was loading
      setTeam((t) => ({ list: r.data || [], newEmail: t?.newEmail || "" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load team"); setTeam(null); }
  };
  const addLabelScanner = async () => {
    const email = (team?.newEmail || "").trim().toLowerCase();
    if (!email || !/@/.test(email)) return toast.error("Enter a valid email");
    try {
      await authApi.post("/admin/label-scanners", { email });
      const r = await authApi.get("/admin/label-scanners");
      setTeam({ list: r.data || [], newEmail: "" });
      toast.success(`${email} whitelisted as label scanner`);
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const removeLabelScanner = async (email) => {
    try {
      await authApi.delete(`/admin/label-scanners/${encodeURIComponent(email)}`);
      setTeam((t) => ({ ...t, list: t.list.filter((x) => x.email !== email) }));
      toast.success("Removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const viewAsBrand = async () => {
    if (!brandId || isAll) return toast.error("Pick a specific brand");
    try {
      const r = await authApi.post(`/admin/impersonate/${brandId}`);
      const url = `/pod-portal?imp=${encodeURIComponent(r.data.session_token)}`;
      window.open(url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Impersonation failed"); }
  };
  const deleteBrand = async () => {
    if (!brandId || isAll) return toast.error("Pick a specific brand");
    if (!brand) return;
    const label = brand.brand_name ? `${brand.brand_name} (${brand.email})` : brand.email;
    const first = window.confirm(
      `Delete brand "${label}"?\n\nThis removes the user + all login sessions. Their past shipments and wallet history stay for audit but become orphaned. This cannot be undone.`
    );
    if (!first) return;
    const typed = window.prompt(`Type the brand's email to confirm:\n${brand.email}`);
    if ((typed || "").trim().toLowerCase() !== (brand.email || "").toLowerCase()) {
      return toast.error("Email did not match — brand not deleted");
    }
    try {
      const r = await authApi.delete(`/admin/brands/${brandId}`);
      toast.success(`Deleted ${r.data.email} · ${r.data.shipments_retained} shipments + ${r.data.wallet_txns_retained} txns kept for audit`);
      setBrandId("");
      await loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const generatePackingList = async () => {
    if (!brandId) return toast.error("Select a brand");
    if (packingBusy) return;
    setPackingBusy(true);
    const toastId = toast.loading("Generating packing list… (self-shipped brands may take up to a minute while we sync Shiprocket)");
    try {
      const r = await authApi.post("/admin/packing-lists", { user_id: brandId }, { timeout: 90000 });
      setPackingList(r.data);
      toast.success(`Packing list: ${r.data.items.length} product(s), ${r.data.total_units} unit(s)`, { id: toastId });
      refreshAll();
    } catch (e) {
      const msg = e.code === "ECONNABORTED"
        ? "The server took too long. It may still finish in the background — refresh and try again in a minute."
        : (e.response?.data?.detail || "Failed to generate packing list");
      toast.error(msg, { id: toastId });
    } finally {
      setPackingBusy(false);
    }
  };

  const printPackingList = () => {
    if (!packingList) return;
    const rows = packingList.items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${it.image ? `<img src="${it.image}" style="width:44px;height:44px;object-fit:cover;border:1px solid #ccc"/>` : "—"}</td>
        <td style="font-family:monospace">${it.sku || "—"}</td>
        <td>${(it.name || "")}${it.variant ? " · " + it.variant : ""}</td>
        <td style="text-align:right;font-weight:700">${it.quantity}</td>
      </tr>`).join("");
    const html = `<html><head><title>Packing List</title>
      <style>body{font-family:Poppins,Arial,sans-serif;padding:24px}h1{text-transform:uppercase}
      table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}
      th{background:#111;color:#fff;text-transform:uppercase;font-size:11px}</style></head>
      <body><h1>Packing List</h1>
      <p><b>${packingList.brand_name || packingList.brand_email}</b> · ${packingList.order_count} order(s) · ${packingList.total_units} unit(s)<br/>
      ${new Date(packingList.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} · Batch ${packingList.batch_id}</p>
      <table><thead><tr><th>#</th><th>Image</th><th>SKU</th><th>Product</th><th style="text-align:right">Qty</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print()}</script></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const syncShiprocket = async () => {
    setSrSyncing(true);
    try {
      const start = await authApi.post("/admin/shiprocket/sync", {});
      if (start.data.status === "already_running") {
        toast.info("A Shiprocket sync is already running…");
      } else {
        toast.info("Shiprocket sync started — this can take a minute…");
      }
      const poll = async (tries) => {
        if (tries <= 0) { setSrSyncing(false); toast.warning("Sync is taking a while — check back shortly."); return; }
        const r = await authApi.get("/admin/shiprocket/sync-status");
        const st = r.data;
        if (st.status === "done") {
          toast.success(`Shiprocket: ${st.archived_removed} archived removed, ${st.custom_unassigned} panel order(s) queued (${st.active_orders} active)`);
          setSrSyncing(false);
          setSrStatus(st);
          loadUnassigned();
          refreshAll();
        } else if (st.status === "error") {
          toast.error(`Shiprocket sync error: ${st.error || "unknown"}`);
          setSrSyncing(false);
        } else {
          setTimeout(() => poll(tries - 1), 5000);
        }
      };
      setTimeout(() => poll(60), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shiprocket sync failed");
      setSrSyncing(false);
    }
  };

  const loadUnassigned = async () => {
    try { const r = await authApi.get("/admin/shiprocket/unassigned"); setSrUnassigned(r.data); }
    catch (e) { /* noop */ }
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillSales = async () => {
    setBackfilling(true);
    try {
      const r = await authApi.post("/admin/shipments/backfill-sale-amount", {});
      const d = r.data || {};
      toast.success(`Sales backfill: ${d.updated || 0} of ${d.scanned || 0} shipments enriched across ${d.stores || 0} store(s)`);
      if ((d.errors || []).length) toast.warning(`${d.errors.length} store(s) skipped — check logs`);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const [assignDialog, setAssignDialog] = useState(null);
  // shape: { mode: 'assign' | 'reassign' | 'bulk', soid?, shipmentId?, orderName?, ids?, currentUid? }
  const [assignTargetUid, setAssignTargetUid] = useState("");

  const openAssignDialog = (mode, extra = {}) => {
    setAssignTargetUid("");
    setAssignDialog({ mode, ...extra });
  };
  const closeAssignDialog = () => { setAssignDialog(null); setAssignTargetUid(""); };

  const brandLabel = (uid) => {
    if (!uid) return "";
    const b = brands.find((x) => x.user_id === uid);
    return b ? (b.brand_name ? `${b.brand_name} (${b.email})` : b.email) : uid;
  };

  const assignUnassigned = async (soid, uid) => {
    if (!uid) return toast.error("Pick a brand");
    try {
      await authApi.post("/admin/shiprocket/assign", { shiprocket_order_id: soid, user_id: uid });
      toast.success("Order assigned to brand");
      loadUnassigned();
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
  };

  const reassignShipment = async (shipmentId, uid) => {
    if (!uid) return toast.error("Pick a brand");
    try {
      const r = await authApi.post(`/admin/shipments/${shipmentId}/reassign`, { user_id: uid });
      if (r.data?.reassigned === false) {
        toast.info(r.data?.detail || "No change");
      } else {
        toast.success(`Reassigned · new charge ₹${r.data.new_charge}`);
      }
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
  };

  const runAssignDialog = async () => {
    if (!assignDialog || !assignTargetUid) return;
    const d = assignDialog;
    if (d.mode === "assign") await assignUnassigned(d.soid, assignTargetUid);
    else if (d.mode === "reassign") await reassignShipment(d.shipmentId, assignTargetUid);
    else if (d.mode === "bulk") {
      try {
        const r = await authApi.post("/admin/shiprocket/assign-bulk", { shiprocket_order_ids: d.ids, user_id: assignTargetUid });
        toast.success(`${r.data.assigned} order(s) assigned`);
        setSrSelected([]);
        loadUnassigned();
        refreshAll();
      } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
    }
    closeAssignDialog();
  };

  const srFiltered = srUnassigned.filter((o) => {
    const t = srFilterText.trim().toLowerCase();
    if (!t) return true;
    const name = String(o.order_name || "").toLowerCase();
    return srFilterMode === "contains" ? name.includes(t) : !name.includes(t);
  });
  const srAllSelected = srFiltered.length > 0 && srFiltered.every((o) => srSelected.includes(o.shiprocket_order_id));
  const srToggle = (id) => setSrSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const srToggleAll = () => setSrSelected(srAllSelected ? [] : srFiltered.map((o) => o.shiprocket_order_id));

  const bulkAssign = () => {
    if (!srBulkBrand) return toast.error("Pick a brand to assign to");
    const ids = srSelected.filter((id) => srFiltered.some((o) => o.shiprocket_order_id === id));
    if (ids.length === 0) return toast.error("No orders selected");
    openAssignDialog("bulk", { ids, currentUid: null });
    setAssignTargetUid(srBulkBrand);
  };


  useEffect(() => { loadUnassigned(); }, []);

  const downloadReport = async () => {
    if (!brandId) return toast.error("Select a brand");
    try {
      const res = await authApi.get("/admin/shipments/report", {
        params: { user_id: brandId, start: repStart || undefined, end: repEnd || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `billing_${repStart || "all"}_${repEnd || "all"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error("Failed to download report"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><img src={LOGO_URL} alt="logo" className="w-9 h-9 rounded-full border border-slate-700" /><span className="font-extrabold uppercase tracking-tight text-sm">Shipments & Billing</span></Link>
          <div className="flex items-center gap-3">
            <button onClick={refreshAll} className="flex items-center gap-1.5 text-xs font-bold uppercase bg-[#FFD400] text-slate-950 px-3 py-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <Link to="/admin-pod" className="flex items-center gap-1.5 text-xs font-bold uppercase hover:text-[#FFD400]"><Wallet className="w-4 h-4" /> Wallet</Link>
            <Link to="/" className="flex items-center gap-1.5 text-xs font-bold uppercase hover:text-[#FFD400]"><Home className="w-4 h-4" /> Home</Link>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs font-bold uppercase bg-slate-800 hover:bg-slate-700 px-3 py-2"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <h1 className="text-2xl font-extrabold uppercase tracking-tight">Shopify Shipments & Billing</h1>

        <div className="flex flex-wrap items-center gap-2">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="ship-brand" className="grow max-w-md border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
            <option value="">Select a brand…</option>
            <option value="__all__">🌐 All brands — combined pending</option>
            {brands.map((b) => <option key={b.user_id} value={b.user_id}>{b.brand_name ? `${b.brand_name} (${b.email})` : b.email} {b.shopify_connected ? "✓ Shopify" : ""} · ₹{b.wallet_balance}</option>)}
          </select>
          {brandId && !isAll && (
            <button onClick={() => setSettingsOpen(true)} data-testid="brand-settings-btn" title="Brand settings (mode, stores, rates)" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-[#FFD400] px-3 py-2.5 text-xs font-bold uppercase">
              <Settings className="w-4 h-4" /> Settings
            </button>
          )}
          {brandId && !isAll && (
            <button onClick={openWalletHistory} data-testid="wallet-history-btn" title="See every recharge and charge on this brand's wallet" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-emerald-100 px-3 py-2.5 text-xs font-bold uppercase">
              <Wallet className="w-4 h-4" /> Wallet History
            </button>
          )}
          {brandId && !isAll && (
            <button onClick={viewAsBrand} data-testid="view-as-brand-btn" title="Open this brand's portal in a new tab (2h impersonation session)" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-indigo-100 px-3 py-2.5 text-xs font-bold uppercase">
              <ScanLine className="w-4 h-4" /> View as brand
            </button>
          )}
          {brandId && !isAll && (
            <button onClick={deleteBrand} data-testid="delete-brand-btn" title="Permanently remove this brand's user + sessions (shipments & wallet history are retained for audit)" className="flex items-center gap-1.5 border-2 border-rose-600 bg-white text-rose-600 hover:bg-rose-600 hover:text-white px-3 py-2.5 text-xs font-bold uppercase">
              <Trash2 className="w-4 h-4" /> Delete brand
            </button>
          )}
          <button onClick={openTeamModal} data-testid="team-btn" title="Manage label-scanner team" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-fuchsia-100 px-3 py-2.5 text-xs font-bold uppercase">
            <Users className="w-4 h-4" /> Team
          </button>
          <button onClick={backfillSales} disabled={backfilling} data-testid="backfill-sales-btn" title="Fetch total_price from Shopify for any old shipments missing sale_amount" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-emerald-100 px-3 py-2.5 text-xs font-bold uppercase disabled:opacity-50">
            <Download className={`w-4 h-4 ${backfilling ? "animate-pulse" : ""}`} /> {backfilling ? "Backfilling…" : "Backfill Sales"}
          </button>
        </div>

        {brandId && !isAll && (
          <div className="flex flex-wrap items-center gap-2 -mt-2" data-testid="sync-freshness">
            <span className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 text-[11px] font-bold uppercase ${freshnessTone(lastShopifySync)}`} data-testid="freshness-shopify" title={lastShopifySync ? `Last successful Shopify store sync (any store)` : "No Shopify sync yet"}>
              <Store className="w-3.5 h-3.5" /> Shopify · {fmtSyncAt(lastShopifySync)}
            </span>
            {mode === "self" && (
              <span className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 text-[11px] font-bold uppercase ${freshnessTone(lastShiprocketSync)}`} data-testid="freshness-shiprocket" title={lastShiprocketSync ? "Last successful Shiprocket sync" : "No Shiprocket sync yet"}>
                <PackagePlus className="w-3.5 h-3.5" /> Shiprocket · {fmtSyncAt(lastShiprocketSync)}
              </span>
            )}
          </div>
        )}

        {/* Shiprocket (self-shipped ops) — only visible for self-shipped brands */}
        {brandId && !isAll && mode === "self" && (
        <div className="bg-white border-2 border-slate-950 p-5 space-y-3" data-testid="shiprocket-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-extrabold uppercase flex items-center gap-2"><PackagePlus className="w-5 h-5" /> Shiprocket Sync</h2>
              <p className="text-[11px] text-slate-500 font-mono">Removes orders archived on Shiprocket from self-shipped pending lists, and queues panel-created (Custom channel) orders for manual brand assignment.</p>
            </div>
            <button onClick={syncShiprocket} disabled={srSyncing} data-testid="shiprocket-sync" className="flex items-center gap-2 bg-slate-950 text-white text-sm font-bold uppercase px-5 py-2.5 border border-slate-950 hover:bg-slate-800 disabled:opacity-50"><RotateCw className={`w-4 h-4 ${srSyncing ? "animate-spin" : ""}`} /> {srSyncing ? "Syncing…" : "Sync Shiprocket"}</button>
          </div>
          {srUnassigned.length > 0 && (
            <div className="border-2 border-slate-200" data-testid="unassigned-list">
              <p className="bg-amber-100 text-amber-800 text-xs font-bold uppercase px-3 py-2">{srUnassigned.length} Shiprocket panel order(s) need a brand</p>
              {/* filter + bulk-assign bar */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                <input type="checkbox" checked={srAllSelected} onChange={srToggleAll} data-testid="sr-select-all" className="w-4 h-4" title="Select all filtered" />
                <select value={srFilterMode} onChange={(e) => setSrFilterMode(e.target.value)} data-testid="sr-filter-mode" className="border-2 border-slate-950 px-2 py-1.5 text-xs font-bold uppercase">
                  <option value="contains">Order ID contains</option>
                  <option value="not">Does not contain</option>
                </select>
                <input value={srFilterText} onChange={(e) => setSrFilterText(e.target.value)} placeholder="e.g. #10 or CROWN" data-testid="sr-filter-text" className="border-2 border-slate-950 px-2 py-1.5 text-xs grow min-w-[120px]" />
                <span className="text-[11px] font-bold text-slate-500">{srFiltered.length} shown · {srSelected.filter((id) => srFiltered.some((o) => o.shiprocket_order_id === id)).length} selected</span>
                <div className="flex-1" />
                <select value={srBulkBrand} onChange={(e) => setSrBulkBrand(e.target.value)} data-testid="sr-bulk-brand" className="border-2 border-slate-950 px-2 py-1.5 text-xs">
                  <option value="">Assign selected to…</option>
                  {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{b.brand_name ? `${b.brand_name} (${b.email})` : b.email}</option>)}
                </select>
                <button onClick={bulkAssign} data-testid="sr-bulk-assign" className="bg-slate-950 text-white text-xs font-bold uppercase px-4 py-2 border border-slate-950 hover:bg-slate-800">Bulk Assign</button>
              </div>
              <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
                {srFiltered.length === 0 && <p className="px-3 py-4 text-sm text-slate-500">No orders match the filter.</p>}
                {srFiltered.map((o) => (
                  <div key={o.shiprocket_order_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2">
                      <input type="checkbox" checked={srSelected.includes(o.shiprocket_order_id)} onChange={() => srToggle(o.shiprocket_order_id)} data-testid={`sr-select-${o.shiprocket_order_id}`} className="w-4 h-4" />
                      <span className="font-bold">{o.order_name} <span className="text-slate-400 font-mono text-xs">SR#{o.shiprocket_order_id}</span></span>
                    </span>
                    <select data-testid={`assign-${o.shiprocket_order_id}`} onChange={(e) => { if (e.target.value) { openAssignDialog("assign", { soid: o.shiprocket_order_id, orderName: o.order_name, currentUid: null }); setAssignTargetUid(e.target.value); e.target.value = ""; } }} defaultValue="" className="border-2 border-slate-950 px-2 py-1.5 text-xs">
                      <option value="">Assign to brand…</option>
                      {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{b.brand_name ? `${b.brand_name} (${b.email})` : b.email}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {brandId && !isAll && (
          <>
            {brand?.recharge_needed && (
              <div data-testid="recharge-warning" className="flex items-start gap-3 border-2 border-rose-500 bg-rose-50 text-rose-800 px-4 py-3">
                <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-extrabold uppercase">Low balance — recharge needed</p>
                  <p>Unprocessed orders total <b>₹{brand.pending_liability}</b> but wallet balance is only <b>₹{brand.wallet_balance || 0}</b>. Credit this brand's wallet before end-of-day billing to avoid a shortfall.</p>
                </div>
              </div>
            )}

            {/* Add / sync */}
            <div className="bg-white border-2 border-slate-950 p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-extrabold uppercase flex items-center gap-2"><Plus className="w-5 h-5" /> Add Shipment</h2>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-500 block">Sync orders from</label>
                    <input type="date" value={syncFrom} onChange={(e) => setSyncFrom(e.target.value)} data-testid="shopify-sync-from" className="border-2 border-slate-950 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-500 block">Quick window</label>
                    <div className="flex border-2 border-slate-950">
                      <button onClick={() => syncWindow(3)} data-testid="sync-3d" className="px-3 py-2 text-xs font-bold uppercase hover:bg-[#FFD400]">3d</button>
                      <button onClick={() => syncWindow(7)} data-testid="sync-7d" className="px-3 py-2 text-xs font-bold uppercase border-l-2 border-slate-950 hover:bg-[#FFD400]">7d</button>
                      <button onClick={() => syncWindow(14)} data-testid="sync-14d" className="px-3 py-2 text-xs font-bold uppercase border-l-2 border-slate-950 hover:bg-[#FFD400]">14d</button>
                      <button onClick={() => syncWindow(null)} data-testid="sync-all" className="px-3 py-2 text-xs font-bold uppercase border-l-2 border-slate-950 hover:bg-[#FFD400]">All</button>
                    </div>
                  </div>
                  <button onClick={() => syncShopify()} data-testid="shopify-sync" className="flex items-center gap-2 bg-[#FFD400] text-slate-950 text-sm font-bold uppercase px-5 py-2.5 border border-slate-950 hover:bg-[#ffdb33]"><RotateCw className="w-4 h-4" /> Sync from Shopify</button>
                  <button onClick={generatePackingList} disabled={packingBusy} data-testid="packing-list-btn" className="flex items-center gap-2 bg-slate-950 text-white text-sm font-bold uppercase px-5 py-2.5 border border-slate-950 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-wait"><ClipboardList className={`w-4 h-4 ${packingBusy ? "animate-pulse" : ""}`} /> {packingBusy ? "Generating…" : "Packing List"}</button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 font-mono">{mode === "self" ? <>Self-shipped (COD): fetches <b>all non-cancelled</b> orders. Leave date empty to sync all.</> : <>Fetches only orders the seller has <b>shipped with tracking added</b> (excludes in-transit / delivered / cancelled). Leave date empty to sync all.</>}</p>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="grow min-w-[180px]">
                  <label className="text-[10px] font-bold uppercase text-slate-500">Order name / ID</label>
                  <input value={orderName} onChange={(e) => setOrderName(e.target.value)} placeholder="#1001" data-testid="manual-ordername" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm" />
                </div>
                {SIZES.map((s) => (
                  <div key={s} className="w-16">
                    <label className="text-[10px] font-bold uppercase text-slate-500">{s}</label>
                    <input type="number" value={qty[s] || ""} onChange={(e) => setQty({ ...qty, [s]: e.target.value })} data-testid={`qty-${s}`} className="w-full border-2 border-slate-950 px-2 py-2.5 text-sm" />
                  </div>
                ))}
                <button onClick={addManual} data-testid="manual-add" className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase px-6 py-3 border border-slate-950">Add</button>
              </div>
            </div>

            {/* Barcode scan */}
            <div className="bg-white border-2 border-slate-950 p-6 space-y-3">
              <h2 className="font-extrabold uppercase flex items-center gap-2"><ScanLine className="w-5 h-5" /> Barcode Scan → Bill</h2>
              <p className="text-[11px] text-slate-500 font-mono">Scan an order's barcode (or type Order ID / tracking no.) and press Enter. Matching order moves to <b>Shipped &amp; Unbilled</b>. Duplicate scans trigger a warning.</p>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="grow min-w-[220px]">
                  <label className="text-[10px] font-bold uppercase text-slate-500">Scan / enter code</label>
                  <input
                    autoFocus
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanOrder(); } }}
                    placeholder="Scan barcode or type #1001…"
                    data-testid="scan-input"
                    className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm"
                  />
                </div>
                <button onClick={scanOrder} data-testid="scan-submit" className="flex items-center gap-2 bg-slate-950 text-white text-sm font-bold uppercase px-6 py-3 border border-slate-950 hover:bg-slate-800"><ScanLine className="w-4 h-4" /> Scan</button>
              </div>
              {scanResult && (
                <div data-testid="scan-result" className={`border-2 ${scanResult.type === "shipped" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-rose-500 bg-rose-50 text-rose-800"}`}>
                  <div className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="flex items-start gap-2">
                      {scanResult.type === "shipped" ? <Truck className="w-5 h-5 mt-0.5" /> : <AlertTriangle className="w-5 h-5 mt-0.5" />}
                      <div className="text-sm">
                        {scanResult.type === "shipped" && <p><b>{scanResult.order_name}</b> billed to Shipped &amp; Unbilled · ₹{scanResult.charge}</p>}
                        {scanResult.type === "duplicate" && <p><b>Duplicate label!</b> {scanResult.order_name} is already <b>{scanResult.status}</b>. If this is a <b>different</b> order with the same ID, add it manually below. Otherwise ignore this scan to avoid double-billing.</p>}
                        {scanResult.type === "cancelled" && <p><b>{scanResult.order_name}</b> is cancelled. Revert it in the table first if it should be shipped.</p>}
                        {scanResult.type === "not_found" && <p>No order found for <b>"{scanResult.code}"</b>. {mode === "self" ? "Sync from Shiprocket or add it manually." : "Type the order id and sizes to bill it manually."}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {scanResult.type === "not_found" && mode !== "self" && (
                        <button onClick={() => openManualBill(scanResult.code)} data-testid="scan-manual-bill-btn" className="text-xs font-bold uppercase bg-slate-950 text-white hover:bg-slate-800 px-3 py-1.5 border border-slate-950">Bill manually</button>
                      )}
                      <button onClick={() => setScanResult(null)} className="text-current opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {/* Courier callout — huge coloured pill so the packing desk sees which shelf immediately */}
                  {(scanResult.type === "shipped" || scanResult.type === "duplicate") && (() => {
                    const known = normalizeCourier(scanResult.courier);
                    const tone = COURIER_TONE[known] || "bg-slate-800 text-white";
                    return (
                      <div className="border-t-2 border-current/30 bg-white/60 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="scan-courier-row">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">Courier</span>
                        {known ? (
                          <span className={`inline-flex items-center gap-2 text-lg font-extrabold uppercase tracking-wider px-4 py-1.5 border-2 border-slate-950 shadow-[3px_3px_0_0_rgba(15,23,42,0.9)] ${tone}`} data-testid="scan-courier-pill">
                            <Truck className="w-5 h-5" /> {known}
                          </span>
                        ) : (
                          <span className="text-xs font-bold uppercase text-rose-700 bg-rose-100 border-2 border-rose-500 px-2 py-1" data-testid="scan-courier-missing">No courier on file — pick one so it goes on the right shelf</span>
                        )}
                        {mode !== "self" && scanResult.shipment_id && (
                          <select
                            value={known && COURIERS.includes(known) ? known : ""}
                            onChange={(e) => updateCourier(scanResult.shipment_id, e.target.value)}
                            data-testid="scan-courier-select"
                            className="border-2 border-slate-950 px-2 py-1.5 text-xs font-bold uppercase bg-white text-slate-900"
                          >
                            <option value="">Change / set courier…</option>
                            {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* End of day / weekly settlement */}
            <div className="bg-slate-950 text-white border-2 border-slate-950 p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CalendarClock className="w-8 h-8 text-[#FFD400]" />
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{mode === "self" ? "Weekly Payable (COD, processed & unsettled)" : "Shipped & Unbilled (this brand)"}</p>
                  <p className="text-2xl font-extrabold font-mono">{brandEod ? `₹${brandEod.total}` : "₹0"} <span className="text-sm text-slate-400">· {brandEod?.count || 0} order(s)</span></p>
                </div>
              </div>
              {mode === "self"
                ? <button onClick={settleWeek} data-testid="settle-week" disabled={!brandEod} className="bg-[#FFD400] text-slate-950 disabled:opacity-40 text-sm font-bold uppercase px-6 py-3.5 border border-slate-950">Mark Settled (Weekly)</button>
                : <button onClick={chargeDay} data-testid="charge-day" disabled={!brandEod} className="bg-[#FFD400] text-slate-950 disabled:opacity-40 text-sm font-bold uppercase px-6 py-3.5 border border-slate-950">Charge Wallet (End of Day)</button>}
            </div>

            {/* Download report */}
            <div className="bg-white border-2 border-slate-950 p-6 space-y-3" data-testid="report-card">
              <h2 className="font-extrabold uppercase flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Download Billing Report (Excel)</h2>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block">From</label>
                  <input type="date" value={repStart} onChange={(e) => setRepStart(e.target.value)} data-testid="report-start" className="border-2 border-slate-950 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block">To</label>
                  <input type="date" value={repEnd} onChange={(e) => setRepEnd(e.target.value)} data-testid="report-end" className="border-2 border-slate-950 px-3 py-2 text-sm" />
                </div>
                <button onClick={downloadReport} data-testid="report-download" className="flex items-center gap-2 bg-slate-950 text-white text-sm font-bold uppercase px-6 py-3 border border-slate-950 hover:bg-slate-800">Download .xlsx</button>
              </div>
              <p className="text-[11px] text-slate-500 font-mono">Columns: Order date, Order ID, Tracking ID, Fulfilled date, Processed/Scanned date, Charge, Status. Leave dates empty for all.</p>
            </div>

            {/* Shipments table with status tabs */}
            <div className="bg-white border-2 border-slate-950" data-testid="shipments-table">
              <div className="flex flex-wrap border-b-2 border-slate-950">
                {TABS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setActiveTab(t)}
                    data-testid={`tab-${t}`}
                    className={`px-5 py-3 text-xs font-bold uppercase tracking-wide border-r border-slate-200 ${activeTab === t ? "bg-[#FFD400] text-slate-950" : "bg-white text-slate-500 hover:bg-slate-100"}`}
                  >{t} ({tabCount(t)})</button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-bold uppercase text-slate-500">Filter date</label>
                  <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="table-date-filter" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
                  {dateFilter && <button onClick={() => setDateFilter("")} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-950">Clear</button>}
                </div>
                <div className="flex-1" />
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="bulk-bar">
                    <span className="text-xs font-bold uppercase text-slate-600">{selectedIds.filter((id) => tabShipments.some((s) => s.shipment_id === id)).length} selected</span>
                    {activeTab === "pending" && <button onClick={() => bulkAction("ship")} data-testid="bulk-ship" className="flex items-center gap-1 bg-[#FFD400] text-slate-950 text-[11px] font-bold uppercase px-3 py-1.5 border border-slate-950"><Truck className="w-3.5 h-3.5" /> Ship</button>}
                    {activeTab === "pending" && <button onClick={() => bulkAction("cancel")} data-testid="bulk-cancel" className="flex items-center gap-1 text-orange-700 border border-orange-400 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-orange-50"><Ban className="w-3.5 h-3.5" /> Cancel</button>}
                    {activeTab !== "billed" && <button onClick={() => bulkAction("delete")} data-testid="bulk-delete" className="flex items-center gap-1 text-rose-700 border border-rose-400 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950 text-white text-left text-xs uppercase"><tr>
                  <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="select-all" className="w-4 h-4" /></th>
                  <th className="px-3 py-3">Order Date</th><th className="px-3 py-3">Order</th><th className="px-3 py-3">Product</th><th className="px-3 py-3">Items</th><th className="px-3 py-3 text-right">Qty</th>
                  <th className="px-3 py-3 text-right">Charge</th><th className="px-3 py-3">Dispatch</th>
                  {(activeTab === "shipped" || activeTab === "billed") && <th className="px-3 py-3">Scanned At</th>}
                  <th className="px-3 py-3">Status</th><th className="px-3 py-3 text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-200">
                  {tabShipments.length === 0 && <tr><td colSpan={(activeTab === "shipped" || activeTab === "billed") ? 11 : 10} className="px-3 py-6 text-center text-slate-500">No {activeTab} orders{dateFilter ? " on this date" : ""}.</td></tr>}
                  {tabShipments.map((s) => (
                    <tr key={s.shipment_id} className={s.status === "cancelled" ? "opacity-60" : ""}>
                      <td className="px-3 py-3"><input type="checkbox" checked={selectedIds.includes(s.shipment_id)} onChange={() => toggleSelect(s.shipment_id)} data-testid={`select-${s.shipment_id}`} className="w-4 h-4" /></td>
                      <td className="px-3 py-3 text-xs font-mono text-slate-600 whitespace-nowrap">{fmtDate(s.order_date)}</td>
                      <td className="px-3 py-3 font-bold">{s.order_name}{stores.length > 1 && s.shop_name ? <span className="block text-[10px] font-normal text-slate-400">{s.shop_name}</span> : null}{s.status === "pending" && <span className="block mt-1"><ReadyBadge s={s} /></span>}</td>
                      <td className="px-3 py-3">
                        {(s.products && s.products.length > 0) ? (
                          <div className="space-y-1.5">
                            {s.products.map((p, i) => (
                              <div key={i} className="flex items-center gap-2">
                                {p.image
                                  ? <img
                                      src={p.image} alt=""
                                      className="w-9 h-9 object-cover border border-slate-300 cursor-zoom-in"
                                      onMouseEnter={(e) => setPreview({ src: p.image, x: e.clientX, y: e.clientY })}
                                      onMouseMove={(e) => setPreview((pr) => pr ? { ...pr, x: e.clientX, y: e.clientY } : pr)}
                                      onMouseLeave={() => setPreview(null)}
                                    />
                                  : <div className="w-9 h-9 bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] text-slate-400">no img</div>}
                                <span className="text-[11px] text-slate-700 leading-tight max-w-[160px] truncate" title={`${p.title}${p.variant ? " · " + p.variant : ""}`}>{p.quantity > 1 ? `${p.quantity}× ` : ""}{p.title}{p.variant ? ` · ${p.variant}` : ""}</span>
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-600 font-mono text-xs">{sizesSummary(s.sizes)}</td>
                      <td className="px-3 py-3 text-right font-mono">{s.quantity}</td>
                      <td className="px-3 py-3 text-right font-extrabold font-mono">₹{s.charge}</td>
                      <td className="px-3 py-3 text-xs">{s.expected_ship_date ? <span>{s.expected_ship_date}<br/><span className={`text-[10px] font-bold ${s.same_day ? "text-emerald-600" : "text-amber-600"}`}>{s.same_day ? "Same-day" : "Next-day"}</span></span> : "—"}</td>
                      {(activeTab === "shipped" || activeTab === "billed") && (
                        <td className="px-3 py-3 text-xs font-mono text-slate-600 whitespace-nowrap" data-testid={`scanned-at-${s.shipment_id}`}>{fmtDate(s.shipped_at)}</td>
                      )}
                      <td className="px-3 py-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                        {s.status === "pending" && s.label_printed_at && (
                          <span data-testid={`label-printed-${s.shipment_id}`} className="ml-1 inline-block text-[9px] font-bold uppercase px-2 py-0.5 bg-indigo-600 text-white" title={`Label printed ${new Date(s.label_printed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`}>
                            Label · {s.label_scanned_by_name || "team"} · {new Date(s.label_printed_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {s.status === "pending" && <>
                            <button onClick={() => shipOne(s.shipment_id)} data-testid={`ship-${s.shipment_id}`} className="flex items-center gap-1 bg-[#FFD400] text-slate-950 text-[11px] font-bold uppercase px-3 py-1.5 border border-slate-950"><Truck className="w-3.5 h-3.5" /> Ship</button>
                            {s.source === "shiprocket" && (
                              <button onClick={() => openAssignDialog("reassign", { shipmentId: s.shipment_id, orderName: s.order_name, currentUid: s.user_id })} data-testid={`reassign-${s.shipment_id}`} title="Reassign to another brand" className="flex items-center gap-1 border border-slate-950 text-slate-950 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-slate-100"><Layers className="w-3.5 h-3.5" /> Reassign</button>
                            )}
                            <button onClick={() => cancelOne(s.shipment_id)} data-testid={`cancel-${s.shipment_id}`} title="Cancel order" className="text-orange-600 hover:text-orange-700"><Ban className="w-4 h-4" /></button>
                            <button onClick={() => deleteOne(s.shipment_id)} title="Delete" className="text-rose-600 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                          </>}
                          {s.status === "cancelled" && (
                            <button onClick={() => revertOne(s.shipment_id)} data-testid={`revert-${s.shipment_id}`} className="flex items-center gap-1 border border-slate-950 text-slate-950 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-slate-100"><Undo2 className="w-3.5 h-3.5" /> Revert</button>
                          )}
                          {s.status === "shipped" && s.tracking_number && <span className="text-[11px] font-mono text-slate-500">{s.tracking_number}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}

        {isAll && (
          <div className="bg-white border-2 border-slate-950" data-testid="all-brands-table">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-950 text-white px-5 py-3">
              <h2 className="font-extrabold uppercase flex items-center gap-2"><Layers className="w-5 h-5" /> All Brands — Pending ({allPending.length})</h2>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-bold uppercase text-slate-300">Filter date</label>
                <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="all-date-filter" className="border-2 border-white bg-slate-900 text-white px-2 py-1 text-sm" />
                {dateFilter && <button onClick={() => setDateFilter("")} className="text-xs font-bold uppercase text-[#FFD400]">Clear</button>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase"><tr>
                  <th className="px-3 py-3">Order Date</th><th className="px-3 py-3">Brand</th><th className="px-3 py-3">Order</th>
                  <th className="px-3 py-3">Product</th><th className="px-3 py-3">Items</th><th className="px-3 py-3 text-right">Qty</th><th className="px-3 py-3">Ready?</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-200">
                  {allPending.length === 0 && <tr><td colSpan="7" className="px-3 py-6 text-center text-slate-500">No pending orders{dateFilter ? " on this date" : ""}.</td></tr>}
                  {allPending.map((s) => (
                    <tr key={s.shipment_id} data-testid={`all-row-${s.shipment_id}`}>
                      <td className="px-3 py-3 text-xs font-mono text-slate-600 whitespace-nowrap">{fmtDate(s.order_date)}</td>
                      <td className="px-3 py-3 text-xs font-semibold text-slate-700">{brandNameOf(s.user_id)}{s.shop_name ? <span className="block text-[10px] font-normal text-slate-400">{s.shop_name}</span> : null}</td>
                      <td className="px-3 py-3 font-bold">{s.order_name}</td>
                      <td className="px-3 py-3">
                        {(s.products && s.products.length > 0) ? (
                          <div className="space-y-1">
                            {s.products.map((p, i) => (
                              <div key={i} className="flex items-center gap-2">
                                {p.image
                                  ? <img src={p.image} alt="" className="w-8 h-8 object-cover border border-slate-300" />
                                  : <div className="w-8 h-8 bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] text-slate-400">no img</div>}
                                <span className="text-[11px] text-slate-700 leading-tight max-w-[180px] truncate" title={`${p.title}${p.variant ? " · " + p.variant : ""}`}>{p.quantity > 1 ? `${p.quantity}× ` : ""}{p.title}{p.variant ? ` · ${p.variant}` : ""}</span>
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-600 font-mono text-xs">{sizesSummary(s.sizes)}</td>
                      <td className="px-3 py-3 text-right font-mono">{s.quantity}</td>
                      <td className="px-3 py-3"><ReadyBadge s={s} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {preview && (
          <img
            src={preview.src}
            alt=""
            style={previewStyle(preview)}
            className="w-64 h-64 object-cover border-2 border-slate-950 shadow-2xl bg-white"
          />
        )}

        {packingList && (
          <div className="fixed inset-0 z-[900] bg-black/60 flex items-center justify-center p-4" data-testid="packing-modal" onClick={() => setPackingList(null)}>
            <div className="bg-white border-2 border-slate-950 w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between bg-slate-950 text-white px-5 py-3">
                <div>
                  <h3 className="font-extrabold uppercase flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Packing List</h3>
                  <p className="text-[11px] text-slate-300 font-mono">{packingList.order_count} order(s) · {packingList.total_units} unit(s) · {packingList.items.length} product(s)</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={printPackingList} data-testid="packing-print" className="flex items-center gap-1.5 bg-[#FFD400] text-slate-950 text-xs font-bold uppercase px-3 py-2"><Printer className="w-4 h-4" /> Print</button>
                  <button onClick={() => setPackingList(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-left text-xs uppercase sticky top-0"><tr>
                    <th className="px-3 py-2">Image</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-200">
                    {packingList.items.map((it, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{it.image ? <img src={it.image} alt="" className="w-11 h-11 object-cover border border-slate-300" /> : <div className="w-11 h-11 bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] text-slate-400">no img</div>}</td>
                        <td className="px-3 py-2 font-mono text-xs">{it.sku || "—"}</td>
                        <td className="px-3 py-2 max-w-[280px] truncate" title={`${it.name}${it.variant ? " · " + it.variant : ""}`}>{it.name}{it.variant ? ` · ${it.variant}` : ""}</td>
                        <td className="px-3 py-2 text-right font-extrabold font-mono">{it.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Settings modal (per-brand: mode, stores, rates) */}
        {settingsOpen && brandId && !isAll && (
          <div className="fixed inset-0 z-50 bg-slate-950/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="brand-settings-modal" onClick={() => setSettingsOpen(false)}>
            <div className="bg-slate-50 border-2 border-slate-950 w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-4 bg-slate-950 text-white">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Brand Settings</p>
                  <p className="text-sm font-extrabold uppercase truncate">{brand?.brand_name || brand?.email}</p>
                </div>
                <button onClick={() => setSettingsOpen(false)} data-testid="brand-settings-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-2 border-2 border-white"><X className="w-4 h-4" /></button>
              </div>

              <div className="p-5 space-y-5">
                {/* Shipping mode toggle */}
                <div className="bg-white border-2 border-slate-950 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="mode-card">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Shipping / Billing Mode</p>
                    <p className="text-sm text-slate-600 font-medium">{mode === "self" ? "Self-shipped (COD) — no wallet, weekly settlement. Syncs all non-cancelled orders." : "Brand-shipped — wallet billing. Syncs only tracking-added orders."}</p>
                  </div>
                  <div className="flex border-2 border-slate-950">
                    <button onClick={() => setMode("brand")} data-testid="mode-brand" className={`px-4 py-2 text-xs font-bold uppercase ${mode === "brand" ? "bg-[#FFD400] text-slate-950" : "bg-white text-slate-500 hover:bg-slate-100"}`}>Brand-shipped</button>
                    <button onClick={() => setMode("self")} data-testid="mode-self" className={`px-4 py-2 text-xs font-bold uppercase border-l-2 border-slate-950 ${mode === "self" ? "bg-[#FFD400] text-slate-950" : "bg-white text-slate-500 hover:bg-slate-100"}`}>Self-shipped (COD)</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Shopify connection */}
                  <div className="bg-white border-2 border-slate-950 p-5 space-y-3">
                    <h2 className="font-extrabold uppercase flex items-center gap-2"><Store className="w-5 h-5" /> Shopify Stores {brand?.shopify_connected && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5">{stores.length} CONNECTED</span>}</h2>
                    {stores.length > 0 && (
                      <div className="border-2 border-slate-200 divide-y divide-slate-200" data-testid="stores-list">
                        {stores.map((st) => {
                          const ok = st.last_sync_ok;
                          const dot = ok === true ? "bg-emerald-500" : ok === false ? "bg-rose-500" : "bg-slate-300";
                          return (
                          <div key={st.store_id} className="flex items-start justify-between gap-2 px-3 py-2.5 text-sm" data-testid={`store-row-${st.store_id}`}>
                            <div className="min-w-0">
                              <span className="flex items-center gap-2 font-medium">
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} data-testid={`store-status-${st.store_id}`} title={ok === true ? "Last sync OK" : ok === false ? "Last sync failed" : "Not synced yet"} />
                                <b className="truncate">{st.shop_name}</b>
                                <span className="text-slate-400 font-mono text-xs truncate">{st.domain}</span>
                              </span>
                              <span className="block pl-4 text-[11px] font-mono text-slate-500">
                                {ok === false
                                  ? <span className="text-rose-600">✕ {st.last_sync_error || "sync failed"} · {fmtSyncAt(st.last_sync_at)}</span>
                                  : <>Synced {fmtSyncAt(st.last_sync_at)}{typeof st.last_order_count === "number" ? ` · ${st.last_order_count} order(s)` : ""}</>}
                              </span>
                            </div>
                            <button onClick={() => removeStore(st.store_id)} data-testid={`store-remove-${st.store_id}`} className="text-rose-600 hover:text-rose-700 shrink-0"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        );})}
                      </div>
                    )}
                    <p className="text-[11px] font-bold uppercase text-slate-500">Add a store</p>
                    <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourstore.myshopify.com" data-testid="shopify-domain" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm" />
                    <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID" data-testid="shopify-client-id" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm" />
                    <input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Client Secret" type="password" data-testid="shopify-client-secret" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm" />
                    <p className="text-[11px] text-slate-500 font-mono">A brand can have multiple stores — add each one here. Dev Dashboard → app → scopes → Install → copy Client ID &amp; Secret.</p>
                    <button onClick={connectShopify} data-testid="shopify-connect" className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase px-6 py-3 border border-slate-950">Add / Update Store</button>
                  </div>

                  {/* Size rates */}
                  <div className="bg-white border-2 border-slate-950 p-5 space-y-3">
                    <h2 className="font-extrabold uppercase">Custom Size Rates (₹/pc)</h2>
                    <div className="grid grid-cols-4 gap-2">
                      {RATE_KEYS.map((k) => (
                        <div key={k}>
                          <label className="text-[10px] font-bold uppercase text-slate-500">{k}</label>
                          <input type="number" value={rates[k] ?? ""} onChange={(e) => setRates({ ...rates, [k]: e.target.value })} data-testid={`rate-${k}`} className="w-full border-2 border-slate-950 px-2 py-2 text-sm" />
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono">"default" is used for any size not listed. Charge = qty × size rate.</p>
                    <button onClick={saveRates} data-testid="rates-save" className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase px-6 py-3 border border-slate-950">Save Rates</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Assign / Reassign confirmation dialog */}
        {assignDialog && (
          <div className="fixed inset-0 z-[950] bg-slate-950/70 flex items-center justify-center p-4" data-testid="assign-dialog" onClick={closeAssignDialog}>
            <div className="bg-white border-2 border-slate-950 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-950 text-white">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">
                    {assignDialog.mode === "reassign" ? "Reassign order" : assignDialog.mode === "bulk" ? `Bulk assign · ${assignDialog.ids.length} orders` : "Assign order"}
                  </p>
                  {assignDialog.orderName && <p className="text-sm font-extrabold uppercase truncate">{assignDialog.orderName}</p>}
                </div>
                <button onClick={closeAssignDialog} data-testid="assign-dialog-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-1.5 border-2 border-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 space-y-3">
                {assignDialog.mode === "reassign" && (
                  <p className="text-xs font-mono text-slate-500">Currently assigned to <b className="text-slate-800">{brandLabel(assignDialog.currentUid)}</b>. Only Shiprocket-source pending orders can be moved. The shipping charge will be recomputed with the new brand's size rates.</p>
                )}
                {assignDialog.mode !== "reassign" && (
                  <p className="text-xs font-mono text-slate-500">Please confirm the brand this order will be assigned to.</p>
                )}
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{assignDialog.mode === "reassign" ? "Move to brand" : "Assign to brand"}</label>
                <select value={assignTargetUid} onChange={(e) => setAssignTargetUid(e.target.value)} data-testid="assign-dialog-brand" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                  <option value="">Select a brand…</option>
                  {brands.filter((b) => b.role !== "admin" && b.user_id !== assignDialog.currentUid).map((b) => (
                    <option key={b.user_id} value={b.user_id}>{b.brand_name ? `${b.brand_name} (${b.email})` : b.email}</option>
                  ))}
                </select>
                {assignTargetUid && (
                  <div className="bg-amber-50 border-2 border-amber-400 p-3 text-sm">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-amber-800 mb-1">Confirm</p>
                    <p className="text-slate-800">
                      {assignDialog.mode === "bulk"
                        ? <>Assign <b>{assignDialog.ids.length}</b> order(s) to <b>{brandLabel(assignTargetUid)}</b>?</>
                        : assignDialog.mode === "reassign"
                          ? <>Move this order to <b>{brandLabel(assignTargetUid)}</b>?</>
                          : <>Assign this order to <b>{brandLabel(assignTargetUid)}</b>?</>}
                    </p>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={closeAssignDialog} data-testid="assign-dialog-cancel" className="border-2 border-slate-950 bg-white hover:bg-slate-100 px-4 py-2 text-xs font-bold uppercase">No, cancel</button>
                  <button onClick={runAssignDialog} disabled={!assignTargetUid} data-testid="assign-dialog-confirm" className="bg-slate-950 text-white hover:bg-slate-800 disabled:opacity-40 px-4 py-2 text-xs font-bold uppercase">Yes, proceed</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Manual bill dialog (brand-shipped only): shipment not in Shopify → admin types
            order id + sizes and it lands straight into Shipped & Unbilled. */}
        {manualBill && brandId && !isAll && (
          <div className="fixed inset-0 z-[950] bg-slate-950/70 flex items-center justify-center p-4" data-testid="manual-bill-dialog" onClick={closeManualBill}>
            <div className="bg-white border-2 border-slate-950 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-950 text-white">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Manual bill (not on Shopify)</p>
                  <p className="text-sm font-extrabold uppercase truncate">{brand?.brand_name || brand?.email}</p>
                </div>
                <button onClick={closeManualBill} data-testid="manual-bill-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-1.5 border-2 border-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Order ID</label>
                  <input value={manualBill.order_name} onChange={(e) => setManualBill((m) => ({ ...m, order_name: e.target.value }))} data-testid="manual-bill-order-id" placeholder="e.g. 10231 or CUSTOM-42" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-mono mt-1" />
                  <p className="text-[10px] text-slate-400 font-mono mt-1">Whatever the label / invoice number reads — used only for reconciliation.</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sizes &amp; quantities</label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {RATE_KEYS.map((k) => (
                      <div key={k}>
                        <label className="text-[10px] font-bold uppercase text-slate-500">{k}</label>
                        <input type="number" min="0" value={manualBill.sizes[k] ?? 0} onChange={(e) => setManualBill((m) => ({ ...m, sizes: { ...m.sizes, [k]: e.target.value } }))} data-testid={`manual-bill-size-${k}`} className="w-full border-2 border-slate-950 px-2 py-2 text-sm text-center" />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono mt-1">Charge = Σ (qty × size rate) at this brand's custom rates.</p>
                </div>
                {(() => {
                  const rateOf = (k) => Number((brand?.size_rates || {})[k] ?? (brand?.size_rates || {}).default ?? 0);
                  const qty = Object.values(manualBill.sizes).reduce((a, b) => a + (Number(b) || 0), 0);
                  const charge = Object.entries(manualBill.sizes).reduce((sum, [k, v]) => sum + (Number(v) || 0) * rateOf(k), 0);
                  return (
                    <div className="bg-amber-50 border-2 border-amber-400 p-3 text-sm flex items-center justify-between" data-testid="manual-bill-preview">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Preview</p>
                        <p className="text-slate-800 font-mono">{qty} pc · ₹{charge}</p>
                      </div>
                      <p className="text-[10px] font-mono text-slate-500 text-right">Will be inserted as <b>shipped</b> and rolls into the next EOD bill.</p>
                    </div>
                  );
                })()}
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={closeManualBill} data-testid="manual-bill-cancel" className="border-2 border-slate-950 bg-white hover:bg-slate-100 px-4 py-2 text-xs font-bold uppercase">Cancel</button>
                  <button onClick={submitManualBill} disabled={manualBill.submitting} data-testid="manual-bill-confirm" className="bg-slate-950 text-white hover:bg-slate-800 disabled:opacity-40 px-4 py-2 text-xs font-bold uppercase">{manualBill.submitting ? "Billing…" : "Bill this order"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Wallet History dialog — per-brand recharge/charge ledger */}
        {walletHistory && (
          <div className="fixed inset-0 z-[950] bg-slate-950/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="wallet-history-modal" onClick={closeWalletHistory}>
            <div className="bg-white border-2 border-slate-950 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-950 text-white">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Wallet history</p>
                  <p className="text-sm font-extrabold uppercase truncate">{walletHistory.data?.brand?.brand_name || walletHistory.data?.brand?.email || brand?.email}</p>
                </div>
                <button onClick={closeWalletHistory} data-testid="wallet-history-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-1.5 border-2 border-white"><X className="w-4 h-4" /></button>
              </div>

              {walletHistory.data && (
                <div className="grid grid-cols-3 divide-x-2 divide-slate-950 border-b-2 border-slate-950">
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Recharged</p>
                    <p className="text-xl font-black font-mono text-emerald-700" data-testid="wallet-history-total-credit">₹{(walletHistory.data.totals.credit || 0).toLocaleString("en-IN")}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Charged</p>
                    <p className="text-xl font-black font-mono text-rose-700" data-testid="wallet-history-total-debit">₹{(walletHistory.data.totals.debit || 0).toLocaleString("en-IN")}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Current balance</p>
                    <p className="text-xl font-black font-mono text-slate-950" data-testid="wallet-history-balance">₹{(walletHistory.data.brand.wallet_balance || 0).toLocaleString("en-IN")}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200">
                {["all", "credit", "debit"].map((t) => (
                  <button key={t} onClick={() => reloadWalletHistory(t)} data-testid={`wallet-history-filter-${t}`}
                    className={`px-3 py-1.5 text-xs font-bold uppercase border-2 border-slate-950 ${walletHistory.filter === t ? "bg-slate-950 text-white" : "bg-white hover:bg-slate-100"}`}>
                    {t === "all" ? "All" : t === "credit" ? "Recharges only" : "Charges only"}
                  </button>
                ))}
                <div className="flex-1" />
                <span className="text-[11px] font-mono text-slate-500">{walletHistory.data?.totals?.count || 0} entr{(walletHistory.data?.totals?.count || 0) === 1 ? "y" : "ies"}</span>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {walletHistory.loading && <p className="p-6 text-center text-sm text-slate-500">Loading…</p>}
                {!walletHistory.loading && (walletHistory.data?.transactions || []).length === 0 && (
                  <p className="p-6 text-center text-sm text-slate-500">No wallet activity yet.</p>
                )}
                {!walletHistory.loading && (walletHistory.data?.transactions || []).length > 0 && (
                  <table className="min-w-full text-sm" data-testid="wallet-history-table">
                    <thead className="bg-slate-100 text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
                      <tr>
                        <th className="px-4 py-2">When</th>
                        <th className="px-4 py-2">Type</th>
                        <th className="px-4 py-2">Note</th>
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2 text-right">Balance after</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {walletHistory.data.transactions.map((t) => {
                        const isCredit = t.type === "credit";
                        return (
                          <tr key={t.txn_id} data-testid={`wallet-txn-${t.txn_id}`}>
                            <td className="px-4 py-2.5 text-xs font-mono text-slate-500 whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${isCredit ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{t.type}</span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-700">{t.note || "—"}</td>
                            <td className={`px-4 py-2.5 text-right font-mono font-black ${isCredit ? "text-emerald-700" : "text-rose-700"}`}>{isCredit ? "+" : "−"}₹{Number(t.amount || 0).toLocaleString("en-IN")}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-500">₹{Number(t.balance_after || 0).toLocaleString("en-IN")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Team management modal — label-scanner whitelist */}
        {team && (
          <div className="fixed inset-0 z-[950] bg-slate-950/70 flex items-center justify-center p-4" data-testid="team-modal" onClick={() => setTeam(null)}>
            <div className="bg-white border-2 border-slate-950 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-950 text-white">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Label-scanner team</p>
                  <p className="text-sm font-extrabold uppercase">Confirmation staff</p>
                </div>
                <button onClick={() => setTeam(null)} data-testid="team-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-1.5 border-2 border-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-slate-500 font-mono">Whitelisted Gmails get access to <b>/label-scan</b>. They can sync Shopify + Shiprocket and scan printed labels, but cannot mark orders shipped, credit wallets or edit brands.</p>
                <div className="flex items-center gap-2">
                  <input value={team.newEmail} onChange={(e) => setTeam((t) => ({ ...t, newEmail: e.target.value }))} placeholder="team.member@gmail.com" data-testid="team-new-email" className="grow border-2 border-slate-950 px-3 py-2 text-sm" />
                  <button onClick={addLabelScanner} data-testid="team-add" className="bg-slate-950 hover:bg-slate-800 text-white text-xs font-bold uppercase px-3 py-2 border border-slate-950">Add</button>
                </div>
                <div className="border-2 border-slate-200 max-h-64 overflow-y-auto divide-y divide-slate-200" data-testid="team-list">
                  {team.list.length === 0 && <p className="p-4 text-sm text-slate-500 text-center">No label scanners yet.</p>}
                  {team.list.map((s) => (
                    <div key={s.email} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm" data-testid={`team-row-${s.email}`}>
                      <div>
                        <p className="font-bold text-slate-800">{s.email}</p>
                        <p className="text-[10px] text-slate-400 font-mono">Added {s.added_at ? new Date(s.added_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}{s.added_by ? ` by ${s.added_by}` : ""}</p>
                      </div>
                      <button onClick={() => removeLabelScanner(s.email)} data-testid={`team-remove-${s.email}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function AdminShipments() {
  return <RequireAuth adminOnly redirectTarget="/admin-shipments">{() => <ShipmentsContent />}</RequireAuth>;
}
